import apis from '../helpers/apis';
import { container } from '@intigral/domain/lib/DataDomain';
import { DI } from '@intigral/constant/config/DIContainer';
import { IProfileService } from '@intigral/domain/lib/application/services/ProfileService';
import type { IUserFullObject } from '@intigral/types';
import user from '../helpers/user';
import h from '../helpers/common';
import { toError } from '../utils/asyncError';

export const ParentalPinStep = {
    CREATE: 'create/parentalpin',
    CREATE_FULFILLED: 'create/parentalpin/fulfilled',
    CREATE_REJECTED: 'create/parentalpin/rejected',

    GET: 'get/parentalpin',
    GET_FULFILLED: 'get/parentalpin/fulfilled',
    GET_REJECTED: 'get/parentalpin/rejected',

    VALIDATE: 'validate/parentalpin',
    VALIDATE_FULFILLED: 'validate/parentalpin/fulfilled',
    VALIDATE_REJECTED: 'validate/parentalpin/rejected',

    UPDATE: 'update/parentalpin',
    UPDATE_FULFILLED: 'update/parentalpin/fulfilled',
    UPDATE_REJECTED: 'update/parentalpin/rejected',

    GENERATE_OTP: 'account/retrivalotp',
    GENERATE_OTP_FULFILLED: 'account/retrivalotp/fulfilled',
    GENERATE_OTP_REJECTED: 'account/retrivalotp/rejected',

    VERIFY_OTP: 'account/retrivalverifyotp',
    VERIFY_OTP_FULFILLED: 'account/retrivalverifyotp/fulfilled',
    VERIFY_OTP_REJECTED: 'account/retrivalverifyotp/rejected',

    RESET_GENERATE_OTP: 'reset/parentalpin/otp',
    RESET_GENERATE_OTP_FULFILLED: 'reset/parentalpin/otp/fulfilled',
    RESET_GENERATE_OTP_REJECTED: 'reset/parentalpin/otp/rejected',
    RESET_VERIFY_OTP: 'reset/parentalpin/verifyotp',
    RESET_VERIFY_OTP_FULFILLED: 'reset/parentalpin/verifyotp/fulfilled',
    RESET_VERIFY_OTP_REJECTED: 'reset/parentalpin/verifyotp/rejected',
    RESET_WITH_OTP: 'reset/parentalpin/withotp',
    RESET_WITH_OTP_FULFILLED: 'reset/parentalpin/withotp/fulfilled',
    RESET_WITH_OTP_REJECTED: 'reset/parentalpin/withotp/rejected',
};

export const createPinMutationFn = async ({ pin }: { pin: string }) => {
    try {
        const epochMs = Date.now();
        const epochMsStr = epochMs.toString();
        const profileService = container.resolve<IProfileService>(DI.SERVICES.IProfileService);
        const result = await profileService.createParentalLock(user.getAPIKey(), user.getIdentityToken(), {
            epochMs,
            encLock: window.btoa(`${epochMsStr}:${pin}`),
        });
        if (typeof result.status === 'string') return true;
    } catch (error) {
        throw error?.data ?? toError(error);
    }
};

export const validatePinMutationFn = async ({ pin }: { pin: string }) => {
    try {
        const epochMs = Date.now();
        const profileService = container.resolve<IProfileService>(DI.SERVICES.IProfileService);
        const result = await profileService.validateParentalLock(
            user.getAPIKey(),
            user.getIdentityToken(),
            epochMs.toString(),
            window.btoa(`${epochMs}:${pin}`)
        );
        // const result = await apis.getJawwyService().parentalPinService().validateParentalPin(pin);
        if (typeof result.status === 'string') return true;
        return result;
    } catch (error) {
        throw error?.data ?? toError(error);
    }
};

export const useGetParentalPinMutationFn = async (password) => {
    try {
        const epochMs = Date.now();
        const profileService = container.resolve<IProfileService>(DI.SERVICES.IProfileService);
        const result = await profileService.getParentalLock(
            user.getAPIKey(),
            user.getIdentityToken(),
            epochMs.toString(),
            window.btoa(`${epochMs}:${password}`)
        );
        // const result = await apis.getJawwyService().parentalPinService().getParentalPin(password);
        if (typeof result.encLock === 'string') return window.atob(result.encLock).split(':')[1];
    } catch (error) {
        throw error?.data ?? toError(error);
    }
};

export const useUpdateParentalPinMutationFn = async ({ password, pin }: { password: boolean; pin: any }) => {
    try {
        const epochMs = Date.now();
        const profileService = container.resolve<IProfileService>(DI.SERVICES.IProfileService);
        const result = await profileService.updateParentalLock(
            user.getAPIKey(),
            user.getIdentityToken(),
            epochMs.toString(),
            window.btoa(`${epochMs}:${password}`),
            window.btoa(`${epochMs}:${pin}`)
        );
        if (typeof result.status === 'string') return true;
    } catch (error) {
        throw error?.data ?? toError(error);
    }
};

export const useAccountRetrievalGenerateOTPMutationFn = async () => {
    try {
        const userInfo = user.getUserInfo() as IUserFullObject;
        const profileService = container.resolve<IProfileService>(DI.SERVICES.IProfileService);
        if (userInfo?.tierStatus?.paymentMethod === 'DCB') {
            return await profileService.accountB2CRetrievalGenerateOTP(userInfo?.msisdn || userInfo?.username);
        } else {
            return await profileService.accountRetrievalGenerateOTP(
                user.getAPIKey(),
                user.getIdentityToken(),
                user.getUserToken(),
                user.getUsername()
            );
        }
    } catch (error) {
        throw error?.data ?? toError(error);
    }
};

export const useAccountRetrievalVerifyOTPMutationFn = async (params: {
    otp;
    verificationGuid;
    userId?: string;
    productKey?: string;
}) => {
    try {
        const { otp, userId, productKey } = params;
        const userInfo = user.getUserInfo() as IUserFullObject;
        const profileService = container.resolve<IProfileService>(DI.SERVICES.IProfileService);
        if (userInfo?.tierStatus?.paymentMethod === 'DCB') {
            return await profileService.accountB2CRetrievalVerifyOTP(
                user.getAPIKey(),
                otp,
                userInfo?.msisdn || userInfo?.username,
                userId,
                productKey
            );
        } else {
            return await profileService.accountRetrievalVerifyOTP(user.getAPIKey(), user.getIdentityToken(), otp);
        }
    } catch (error) {
        throw error?.data ?? toError(error);
    }
};

export const useResetParentalLockGenerateOTPMutationFn = async () => {
    const apiKey = user.getAPIKey();
    const username = user.getUsername();
    const lang = h.getLang();
    const identityToken = user.getIdentityToken();
    try {
        const profileService = container.resolve<IProfileService>(DI.SERVICES.IProfileService);
        return await profileService.resetParentalLockGenerateOTP(apiKey, identityToken, username, lang);
    } catch (error) {
        throw error?.data ?? toError(error);
    }
};

export const normalizeVerifiedParentalLockPin = (parentalLockPin?: number | string): string | null => {
    if (parentalLockPin == null || parentalLockPin === '') {
        return null;
    }

    const raw = String(parentalLockPin);
    try {
        const decoded = window.atob(raw);
        if (decoded.includes(':')) {
            return decoded.split(':')[1];
        }
        if (/^\d+$/.test(decoded)) {
            return decoded;
        }
    } catch {
        // Not base64 — use the raw pin value from the verify response.
    }

    return raw;
};

export const useResetParentalLockVerifyOTPMutationFn = async (params: { otp: string; verificationGuid: string }) => {
    const apiKey = user.getAPIKey();
    try {
        const profileService = container.resolve<IProfileService>(DI.SERVICES.IProfileService);
        return await profileService.resetParentalLockVerifyOTP(
            apiKey,
            user.getIdentityToken(),
            params.otp,
            params.verificationGuid
        );
    } catch (error) {
        throw error?.data ?? toError(error);
    }
};

export const useResetParentalLockWithOTPMutationFn = async (params: {
    pin: string;
    verificationGuid?: string;
    currentParentalLockPin?: string;
}) => {
    const apiKey = user.getAPIKey();
    try {
        const profileService = container.resolve<IProfileService>(DI.SERVICES.IProfileService);
        const result = await profileService.resetParentalLockWithOTP(apiKey, user.getIdentityToken(), params.pin, {
            verificationGuid: params.verificationGuid,
            currentParentalLockPin: params.currentParentalLockPin,
        });
        if (typeof result?.status === 'string') return result;
        return result;
    } catch (error) {
        throw error?.data ?? toError(error);
    }
};
